// Conjuring a spirit (issue 1531; docs/MRCS/shadowrun-3.md §9 step 17; the book p. 184-188, 266-268): where the table's
// system declares conjuring (`features.combat.conjuring`), a conjurer calls a spirit of a chosen Force. Every step is the
// system's data:
//
// 1. The conjuring test: the skill's dice, less any held back for the drain, against the Force, with the TN modifiers
//    asked and the conjurer's wounds (which the server joins). Each success is one service the spirit owes; none, and
//    no spirit comes. The Force may be at most `most` times the conjurer's Magic.
// 2. The drain: the drain rating (Charisma) and the dice held back against the Force, with its own TN modifiers. Its
//    level is the first of the data's `levels` whose bound (`most` times the drain rating) the Force does not pass; every
//    `stage_per` successes take it one step down, below the lowest none. Stun within the conjurer's Magic, Physical over.
// 3. The spirit: on a success the server puts a token on the table for it, its numbers worked out at the Force from the
//    entry's formulas (`spiritNumbers`): each attribute, the initiative, the condition monitors, the armour, the pools
//    (the services owed counted into the one the data says) and the astral state it arrives in.
//
// The record is a success test's (`TestFacts.conjure`, rules/test.ts): its first side is the conjuring test, so the log,
// privacy and projection are a test's own; the drain lands on the conjurer as the server commits it, and the spirit's
// token after. Nothing here throws a die on its own: the server hands the generator in, a test a scripted list.
import { MAX_POOL, poolDisaster, testDice } from '../dice/pool.js';
import { codesData, parseCodes, parseTestDice } from './system.js';
import { testDiceData } from './test.js';
import { diceSum, diceTermLine, parseDiceTerms } from './magicDice.js';
import { parseThrow, successesOf, throwLine, throwOne } from './staged.js';
export const CONJURE_LIMITS = { name: 80, force: 30, rating: MAX_POOL, modifiers: 8, modifier: 50, label: 40, formula: 16, fields: 16, magic: 30 };
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const KEY_RE = /^[a-z][a-z0-9_]{0,62}$/;
const ADDRESS_RE = /^[a-z][a-z0-9_]{0,62}\/[a-z0-9][a-z0-9-]{0,127}$/;
const FORMULA_RE = /^[A-Za-z]?\s*([+-]\s*\d{1,2})?$|^-?\d{1,2}$/;
// eslint-disable-next-line no-control-regex
const LINE_BAD = /[\u0000-\u001f\u007f]/;
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const isInt = (v, min, max) => typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;
const isText = (v, max) => typeof v === 'string' && v.trim().length >= 1 && v.length <= max && !LINE_BAD.test(v);
const only = (v, keys) => Object.keys(v).every((k) => keys.includes(k));
function parseTerms(v, most = CONJURE_LIMITS.modifiers) {
    if (v === undefined)
        return [];
    if (!Array.isArray(v) || v.length > most)
        return null;
    const out = [];
    for (const x of v) {
        if (!isObj(x) || !only(x, ['label', 'value']) || !isText(x.label, CONJURE_LIMITS.label) || !isInt(x.value, -CONJURE_LIMITS.modifier, CONJURE_LIMITS.modifier))
            return null;
        out.push({ label: x.label.trim(), value: x.value });
    }
    return out;
}
/** A spirit off the wire or a record: exact shape or null. Its formulas are short and each one the reader reads. */
export function parseConjureSpirit(v) {
    if (!isObj(v) || !only(v, ['name', 'ref', 'stats']) || !isText(v.name, CONJURE_LIMITS.name) || (v.ref !== undefined && (typeof v.ref !== 'string' || !ADDRESS_RE.test(v.ref))))
        return null;
    if (!isObj(v.stats) || Object.keys(v.stats).length > CONJURE_LIMITS.fields)
        return null;
    const stats = {};
    for (const [k, f] of Object.entries(v.stats)) {
        if (!KEY_RE.test(k) || typeof f !== 'string' || f.length > CONJURE_LIMITS.formula || !FORMULA_RE.test(f.trim()))
            return null;
        stats[k] = f.trim();
    }
    return { name: v.name.trim(), ...(typeof v.ref === 'string' ? { ref: v.ref } : {}), stats };
}
function parseSkill(s) {
    if (!isObj(s) || !only(s, ['name', 'rating', 'ref']) || !isText(s.name, CONJURE_LIMITS.name) || !isInt(s.rating, 1, CONJURE_LIMITS.rating) || (s.ref !== undefined && (typeof s.ref !== 'string' || !ADDRESS_RE.test(s.ref))))
        return null;
    return { name: s.name.trim(), rating: s.rating, ...(typeof s.ref === 'string' ? { ref: s.ref } : {}) };
}
/** A conjure request from the wire: exact shape or null. Untrusted input, always. */
export function parseConjureRequest(v) {
    if (!isObj(v) || v.kind !== 'spirit.conjure')
        return null;
    if (!only(v, ['kind', 'conjurer', 'spirit', 'force', 'skill', 'reserve', 'modifiers', 'dice', 'drainModifiers', 'magic', 'drainRating', 'private']))
        return null;
    if (!UUID_RE.test(String(v.conjurer)))
        return null;
    const spirit = parseConjureSpirit(v.spirit);
    const skill = parseSkill(v.skill);
    if (!spirit || !skill || !isInt(v.force, 1, CONJURE_LIMITS.force) || !isInt(v.reserve ?? 0, 0, skill.rating - 1))
        return null;
    const modifiers = parseTerms(v.modifiers);
    const drainModifiers = parseTerms(v.drainModifiers);
    const dice = parseDiceTerms(v.dice);
    if (!modifiers || !drainModifiers || !dice)
        return null;
    if ((v.magic !== undefined && !isInt(v.magic, 0, CONJURE_LIMITS.magic)) || (v.drainRating !== undefined && !isInt(v.drainRating, 1, CONJURE_LIMITS.rating)))
        return null;
    if (v.private !== undefined && typeof v.private !== 'boolean')
        return null;
    return {
        kind: 'spirit.conjure', conjurer: String(v.conjurer), spirit, force: v.force, skill, reserve: v.reserve ?? 0, modifiers, ...(dice.length ? { dice } : {}), drainModifiers,
        ...(typeof v.magic === 'number' ? { magic: v.magic } : {}), ...(typeof v.drainRating === 'number' ? { drainRating: v.drainRating } : {}),
        private: v.private === true,
    };
}
export const NO_CONJURE_RULES = "This table's game system names no conjuring, so the table cannot conjure a spirit.";
/**
 * A formula of the spirit family read at the Force (`variable` the letter that stands for it): "F", "F+3", "F - 2", or a
 * plain number. Null when it is none of those.
 */
export function formulaAt(text, variable, force) {
    const t = text.replace(/\s+/g, '');
    if (/^-?\d{1,2}$/.test(t))
        return Number(t);
    if (!t.startsWith(variable))
        return null;
    const rest = t.slice(variable.length);
    if (rest === '')
        return force;
    return /^[+-]\d{1,2}$/.test(rest) ? force + Number(rest) : null;
}
/** The drain's level for a Force (p. 187): the first level whose bound, `most` times the drain rating, the Force does not pass. */
export function conjureDrainLevel(rules, force, rating) {
    for (const l of rules.drain.levels)
        if (l.most === null || force <= l.most * rating)
            return l.level;
    return rules.drain.levels.at(-1).level;
}
const WOUNDED = 'wounded';
/** The label the dice held back for the drain carry on its throw. */
const HELD = 'held';
/**
 * A conjuring made concrete (§9 step 17): the conjuring test and the drain test thrown with the generator by the
 * system's rules, as a success test's record with the conjuring beside it. A string is a refusal in words.
 */
export function makeConjure(req, m, rng) {
    const r = m.rules;
    if (m.magic === null)
        return `The conjurer's ${r.magic.label} could not be read from its sheet: type it.`;
    if (m.drainRating === null)
        return `The conjurer's ${r.drain.rating.label} could not be read from its sheet: type it.`;
    if (r.most && req.force > r.most * m.magic)
        return `The Force may be at most ${r.most * m.magic}: ${r.most} times the conjurer's ${r.magic.label}.`;
    for (const f of r.spirit.attributes) {
        const formula = req.spirit.stats[f];
        if (formula === undefined || formulaAt(formula, r.spirit.variable, req.force) === null)
            return `The spirit's ${f.replace(/_/g, ' ')} is not a formula the table reads.`;
    }
    // The sheet's own dice (issue 1172) add to the dice rolled.
    const dice = req.skill.rating - req.reserve + diceSum(req.dice);
    if (dice < 1)
        return 'The dice the sheet takes off leave none to roll.';
    const terms = [...req.modifiers, ...(m.wound ? [{ label: WOUNDED, value: Math.max(-CONJURE_LIMITS.modifier, Math.min(CONJURE_LIMITS.modifier, m.wound)) }] : [])];
    const cast = throwOne(r.dice, { label: req.skill.name, rating: dice, spent: null, dice, target: req.force, base: 'Force', terms }, rng);
    if (typeof cast === 'string')
        return cast;
    const over = req.force > m.magic;
    const kind = m.kinds.find((k) => k.key === (over ? r.drain.kinds.over : r.drain.kinds.within));
    if (!kind)
        return 'The drain\'s kind of damage is not one of this game system\'s.';
    const held = req.reserve ? { key: HELD, label: req.skill.name, dice: req.reserve } : null;
    const drain = throwOne(r.dice, { label: r.drain.rating.label, rating: m.drainRating, spent: held, dice: m.drainRating + req.reserve, target: req.force, base: 'Force', terms: req.drainModifiers }, rng);
    if (typeof drain === 'string')
        return drain;
    const rules = testDiceData(r.dice);
    const side = {
        name: m.who.name, ...(m.who.token ? { token: m.who.token } : {}), rated: { kind: 'skill', name: req.skill.name, rating: dice, ...(req.skill.ref ? { ref: req.skill.ref } : {}) },
        dice, target: req.force, terms, tn: cast.tn, pool: cast.pool,
    };
    return {
        open: false, sides: [side], rules: { dice: rules, open: null, ties: null },
        conjure: {
            spirit: { ...req.spirit, stats: { ...req.spirit.stats } }, force: req.force, skill: { ...req.skill }, reserve: req.reserve, ...(req.dice?.length ? { extra: req.dice.map((x) => ({ ...x })) } : {}), magic: m.magic,
            drain: { ...drain, level: conjureDrainLevel(r, req.force, m.drainRating), kind: kind.key, kindLabel: kind.label },
            codes: parseCodes(codesData(r.codes)), token: m.token,
        },
    };
}
const throwOf = (f) => {
    const s = f.sides[0];
    return { label: s.rated.name, rating: s.rated.rating, spent: null, dice: s.dice, target: s.target ?? 1, base: 'Force', terms: s.terms, tn: s.tn ?? 1, pool: s.pool };
};
export function conjureOutcome(f) {
    const c = f.conjure;
    const t = throwOf(f);
    const services = successesOf(f.rules.dice, t);
    const dd = parseTestDice(f.rules.dice);
    const concrete = dd && t.dice ? testDice(dd, { dice: t.dice, target: t.tn }) : null;
    const disaster = concrete ? poolDisaster(concrete, t.pool) : false;
    const levels = c.codes.levels;
    const from = levels.findIndex((l) => l.key === c.drain.level);
    const dSucc = successesOf(f.rules.dice, c.drain);
    const at = from - Math.floor(dSucc / c.codes.stagePer);
    return { services, disaster, drain: { successes: dSucc, from: { ...levels[from] }, level: at >= 0 ? { ...levels[at] } : null, steps: at - from } };
}
/** The drain the conjurer takes: its level's boxes, of the drain's kind. Null for none. */
export function conjureDrain(f) {
    const o = conjureOutcome(f);
    return o.drain.level ? { boxes: o.drain.level.boxes, kind: f.conjure.drain.kind, kindLabel: f.conjure.drain.kindLabel, level: o.drain.level } : null;
}
/**
 * A spirit's numbers worked out from its formulas at the Force (§9 step 17; p. 188, 265-266): each attribute (at least
 * 1), the initiative dice and bonus, the monitors' maximums, the armour under each of its words, and each pool — a sum of
 * attributes divided (rounded down), or the services owed plus a number. A string is a refusal in words.
 */
export function spiritNumbers(rules, pools, stats, force, services) {
    const sp = rules.spirit;
    const at = {};
    for (const f of sp.attributes) {
        const v = stats[f] === undefined ? null : formulaAt(stats[f], sp.variable, force);
        if (v === null)
            return `The spirit's ${f.replace(/_/g, ' ')} is not a formula the table reads.`;
        at[f] = Math.max(1, Math.min(MAX_POOL, v));
    }
    const sheet = sp.attributes.map((f) => ({ purpose: sp.purpose, words: f.split('_'), value: at[f] }));
    sheet.push({ purpose: 'initiative_dice', words: ['initiative', 'dice'], value: sp.initiative.dice });
    for (const p of sp.pools) {
        const rule = pools.find((x) => x.key === p.key);
        if (!rule?.sheet)
            continue;
        const value = p.sum ? Math.floor(p.sum.reduce((n, f) => n + at[f], 0) / (p.divide ?? 1)) : services + (p.services ?? 0);
        sheet.push({ purpose: rule.sheet, words: [p.key, 'pool'], value: Math.max(0, Math.min(MAX_POOL, value)) });
    }
    if (sp.armour)
        for (const w of sp.armour.words)
            sheet.push({ purpose: 'armor_rating', words: [w], value: Math.min(100, force * sp.armour.times) });
    const resources = {};
    for (const [key, m] of Object.entries(sp.monitors)) {
        const max = typeof m === 'number' ? m : at[m];
        resources[key] = { current: max, max };
    }
    return { sheet, resources, initBonus: at[sp.initiative.from] + sp.initiative.add };
}
// ── Stored records ────────────────────────────────────────────────────────
/** Stored conjure facts beside a test's: exact shape or null. The drain's TN is the Force, its level what Force and rating make. */
export function parseConjureFacts(v, rules, side) {
    if (!isObj(v) || !only(v, ['spirit', 'force', 'skill', 'reserve', 'extra', 'magic', 'drain', 'codes', 'token']) || !side)
        return null;
    const extra = v.extra === undefined ? undefined : parseDiceTerms(v.extra);
    if (extra === null || (extra && !extra.length))
        return null;
    const d = parseTestDice(rules);
    const spirit = parseConjureSpirit(v.spirit);
    const skill = parseSkill(v.skill);
    const codes = isObj(v.codes) ? parseCodes({ levels: v.codes.levels, stage_per: v.codes.stagePer, beyond_top: v.codes.beyondTop, ...(v.codes.wielder !== undefined ? { wielder: v.codes.wielder } : {}) }) : null;
    if (!d || !spirit || !skill || !codes || !isInt(v.force, 1, CONJURE_LIMITS.force) || !isInt(v.reserve, 0, skill.rating - 1) || !isInt(v.magic, 0, CONJURE_LIMITS.magic) || !UUID_RE.test(String(v.token)))
        return null;
    // The conjuring side is the skill less the dice held, against the Force.
    if (side.rated.rating !== skill.rating - v.reserve + diceSum(extra) || side.target !== v.force)
        return null;
    const dr = v.drain;
    if (!isObj(dr))
        return null;
    const { level, kind, kindLabel, ...rest } = dr;
    const drain = parseThrow(rest, rules, d);
    if (!drain || drain.target !== v.force || typeof level !== 'string' || !codes.levels.some((l) => l.key === level) || typeof kind !== 'string' || !KEY_RE.test(kind) || !isText(kindLabel, 24))
        return null;
    if (v.reserve !== (drain.spent?.dice ?? 0))
        return null;
    return {
        spirit, force: v.force, skill, reserve: v.reserve, ...(extra ? { extra } : {}), magic: v.magic,
        drain: { ...drain, level, kind, kindLabel: kindLabel }, codes, token: String(v.token),
    };
}
// ── The lines ─────────────────────────────────────────────────────────────
/** The conjuring test's line: "Conjure Fire Elemental at Force 4: Conjuring 6 − 2 held for drain = 4 dice vs TN 4 (Force): … → 2 successes". */
export function conjureLine(f) {
    const c = f.conjure;
    const t = throwOf(f);
    const line = throwLine(`Conjure ${c.spirit.name} at Force ${c.force}`, f.rules.dice, t);
    const extra = (c.extra ?? []).map((x) => ` ${x.value < 0 ? '−' : '+'} ${Math.abs(x.value)} ${x.label}`).join('');
    return c.reserve || extra ? line.replace(`${t.label} ${t.rating} vs`, `${t.label} ${c.skill.rating}${c.reserve ? ` − ${c.reserve} held for drain` : ''}${extra} = ${t.dice} dice vs`) : line;
}
/** The services line: "2 services owed", "No successes: no spirit comes", "Disaster: no spirit comes". */
export function servicesLine(f) {
    const o = conjureOutcome(f);
    if (!o.services)
        return o.disaster ? 'Disaster: no spirit comes' : 'No successes: no spirit comes';
    return `${f.conjure.spirit.name} comes: ${o.services} ${o.services === 1 ? 'service' : 'services'} owed`;
}
/** The drain test's line: "Drain: Charisma 4 + 2 Conjuring = 6 dice vs TN 4 (Force): … → 3 successes". */
export const conjureDrainLine = (f) => throwLine('Drain', f.rules.dice, f.conjure.drain);
/** The drain's result: "Drain M staged down to L → 1 box Stun", "Drain S staged below L: no drain". */
export function conjureDrainResultLine(f) {
    const o = conjureOutcome(f);
    const from = o.drain.from.abbr;
    if (!o.drain.level)
        return `Drain ${from} staged below ${f.conjure.codes.levels[0].abbr}: no drain`;
    const moved = o.drain.steps ? `${from} staged down to ${o.drain.level.abbr}` : from;
    return `Drain ${moved} → ${o.drain.level.boxes} ${o.drain.level.boxes === 1 ? 'box' : 'boxes'} ${f.conjure.drain.kindLabel}`;
}
/** Every line of a conjuring: the test, the services, the drain and its result. */
export function conjureLines(f) {
    return [...(f.conjure.extra ?? []).map(diceTermLine), conjureLine(f), servicesLine(f), conjureDrainLine(f), conjureDrainResultLine(f)];
}
/** Whether a test's record is a conjuring. */
export const isConjure = (f) => f.conjure !== undefined;
//# sourceMappingURL=conjure.js.map